import { debounce, isEqual } from "lodash-es"
import { observable, reaction, runInAction } from "mobx"
import { Subject } from "rxjs"
import { toast } from "sonner"

import { ArchivedMessagesBlockModel } from "@/components/Chat/ArchivedMessagesBlockModel.ts"
import { CollectionModelExtender } from "@/components/Chat/CollectionModelExtender.ts"
import { TypingIndicatorModel } from "@/components/Chat/TypingIndicatorModel.ts"
import {
  type PromptInputModel,
  PromptInputModelImpl,
  type PromptInputSubmitArgs
} from "@/components/PromptInput/PromptInputModel.ts"
import { nextFocusId } from "@/hooks/useScrollIntoViewEffect.ts"
import { formatDateTime } from "@/lib/date.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { promiseWithResolvers } from "@/lib/promise.ts"
import { compareStringAsInteger, isUserPrincipal, successfulAttachments } from "@/lib/std.ts"

import type { ListModel } from "@/components/Chat/LoadMoreButton.tsx"
import type { CompletionProvider } from "@/components/PromptInput/Completion/completionProviders.ts"
import type { RouterType } from "@/context.ts"
import type {
  AiAgentTaskContextItem,
  AiAgentTaskDomElementIn,
  AiChatEvent,
  AiChatMessage,
  AiChatMessageAttachment,
  AiChatMessageAuthor,
  AiChatMessageContent,
  Batch,
  BatchInfo,
  CPrincipal,
  MessagesSorting,
  TD_MemberProfile,
  UAiChatMessageContent
} from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { Queries } from "@/lib/queries.ts"
import type { IObservableValue } from "mobx"


const log = getLogger("ChatModel")

export type ChatMessageEditingModel = {
  messageId: string

  promptInputModel: PromptInputModel

  cancel(): void
}

export type PendingEditConfirmation = {
  commits: number
  resolve: (proceed: boolean) => void
}

export interface ChatModel extends ListModel {
  messages: IObservableValue<ChatMessageModel[]>
  hasNext: IObservableValue<boolean>
  hasPrev: IObservableValue<boolean>
  isLoadingNext: IObservableValue<boolean>
  isLoadingPrev: IObservableValue<boolean>
  isLoadingInitially: IObservableValue<boolean>
  isResettingCheckpoint: IObservableValue<boolean>
  isReady: IObservableValue<boolean>
  waitingForResponse: IObservableValue<number>
  activeEditingModel: IObservableValue<ChatMessageEditingModel | null>
  liveUpdateDebugEvents: IObservableValue<boolean>
  typingIndicatorModel: TypingIndicatorModel
  pendingEditConfirmation: IObservableValue<PendingEditConfirmation | null>

  sendMessage(message: string, context?: AiAgentTaskContextItem[], attachments?: AiChatMessageAttachment[], elements?: AiAgentTaskDomElementIn[]): Promise<void>

  loadNext(): void

  loadPrev(): void

  terminate(): void

  navigate(messageId: string): void

  read(messageId: string): void

  edit(messageId: string, content: AiChatMessageContent.Text): void

  confirmEditWithRollback(): void

  cancelEditConfirmation(): void
}

export type ChatMessageModel = AiChatMessage & {
  active: boolean
  isNew: boolean
  isLast: boolean
  focusId: string | null
  user: TD_MemberProfile | null
  isMyMessage: boolean
  text: string | null,
  onRetryProcess: () => Promise<void>
  onContinueProcess: () => Promise<void>
  downloadAttachment: (file: AiChatMessageAttachment) => void
  edit: (() => void) | null
  editingModel: ChatMessageEditingModel | null
  archivedBlockModel?: ArchivedMessagesBlockModel
}

export class ChatModelImpl extends Model implements ChatModel {
  private readonly batchSize: number = 40
  private readonly router: RouterType | null
  private readonly queries: Queries
  private readonly context: OrgContext | null
  private readonly rawMessages = new Map<string, AiChatMessage>()
  private readonly messageIdToFocusId = new Map<string, string>()
  private readonly newMessages = new Set<string>()
  private readonly taskId: string
  private readonly updateQueue: ChatModelUpdateRequest[] = []
  private readonly completionProvider: CompletionProvider
  private readonly myPrincipal: Promise<CPrincipal>
  private isProcessing: boolean = false
  private lastReadMessageId: string | null = null
  private readonly readOnly: IObservableValue<boolean> | undefined
  readonly liveUpdateDebugEvents = observable.box(false)

  messages = observable.box<ChatMessageModel[]>([])
  hasNext = observable.box(false)
  hasPrev = observable.box(true)
  isLoadingNext = observable.box(false)
  isLoadingPrev = observable.box(false)
  isLoadingInitially = observable.box(true)
  isResettingCheckpoint = observable.box(false)
  isReady = observable.box(false)
  waitingForResponse = observable.box(0)
  summaryModelEvents = new Subject<AiChatEvent.SummaryEvent>()
  activeEditingModel = observable.box<ChatMessageEditingModel | null>(null)
  pendingEditConfirmation = observable.box<PendingEditConfirmation | null>(null)
  extender = new CollectionModelExtender()
  typingIndicatorModel: TypingIndicatorModel

  constructor(params: {
    router: RouterType | null,
    queries: Queries,
    context: OrgContext | null,
    taskId: string,
    messageId?: string,
    batchSize?: number,
    completionProvider: CompletionProvider,
    readOnly?: IObservableValue<boolean>,
    devMode?: boolean
  }) {
    super("ChatModelImpl")

    this.taskId = params.taskId
    this.batchSize = params.batchSize || 40
    this.router = params.router
    this.queries = params.queries
    this.context = params.context
    this.myPrincipal = this.queries.me()
    this.completionProvider = params.completionProvider
    this.readOnly = params.readOnly
    this.typingIndicatorModel = new TypingIndicatorModel(params.taskId, params.queries)

    void this.startQueueProcessing()
    void this.listenForUpdates(params.messageId ?? null)

    let cancelTimeout: ReturnType<typeof setTimeout> | null = null
    this.lifetime.add(reaction(
      () => ({
        waitingForResponse: this.waitingForResponse.get()
      }),
      ({ waitingForResponse }) => {
        if (waitingForResponse > 0) {
          cancelTimeout = setTimeout(() => {
            runInAction(() => this.waitingForResponse.set(0))
          }, 30_000)
        } else {
          cancelTimeout && clearTimeout(cancelTimeout)
        }
      }
    ), "ChatModelImpl:waitingForResponse")

    this.lifetime.add(reaction(
      () => ({
        editingModel: this.activeEditingModel.get()
      }),
      () => this.scheduleUpdate({ type: "observable-update" })
    ), "ChatModelImpl:activeEditingModel")

    if (params.devMode) {
      void this.initDebugLiveUpdates()
    }
  }

  edit(messageId: string, content: AiChatMessageContent.Text): void {
    const currentEditingModel = this.activeEditingModel.get()
    if (currentEditingModel) {
      currentEditingModel.cancel()
    }
    const editingModelBox = this.activeEditingModel
    runInAction(() => {
      const editingModel: ChatMessageEditingModel = {
        messageId,
        promptInputModel: new PromptInputModelImpl({
          name: `edit:${messageId}`,
          submit: async (args: PromptInputSubmitArgs) => {
            const resolvedAttachments = await successfulAttachments(args.attachments)
            const success = await this.editMessage(messageId, args.firstInputValue, args.context, resolvedAttachments, args.elements)
            if (success) {
              runInAction(() => editingModelBox.set(null))
            }
            return success
          },
          taskId: this.taskId,
          completionProvider: this.completionProvider,
          orgContext: this.context ?? undefined,
          initialState: {
            prompt: copyableTextContent(content) ?? "",
            context: content.contextItems,
            attachments: content.attachments,
            domElements: content.domElements
          },
          persistable: observable.box(false),
          readOnly: this.readOnly,
          router: this.router ?? undefined,
        }),
        cancel: () => {
          const pending = this.pendingEditConfirmation.get()
          if (pending) {
            pending.resolve(false)
          }
          runInAction(() => editingModelBox.set(null))
        }
      }
      this.activeEditingModel.set(editingModel)
      editingModel.promptInputModel.autoFocus.set(true)
    })
  }

  readonly sendMessage = async (message: string, context?: AiAgentTaskContextItem[], attachments?: AiChatMessageAttachment[], elements?: AiAgentTaskDomElementIn[]) => {
    log.info("Send", message)
    const attachmentIds = attachments?.map(attachment => attachment.id)
    try {
      await this.queries.apiClient.sendMessage(this.taskId, message, context || [], attachmentIds || [], elements || [])
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err))
    }
  }

  readonly editMessage = async (messageId: string, message: string, context?: AiAgentTaskContextItem[], attachments?: AiChatMessageAttachment[], elements?: AiAgentTaskDomElementIn[]): Promise<boolean> => {
    const attachmentIds = attachments?.map(attachment => attachment.id)
    const result = await this.queries.apiClient.editMessage(messageId, message, context || [], attachmentIds || [], elements || [], false)

    if (result.className === "ConfirmRollback") {
      const { promise, resolve } = promiseWithResolvers<boolean>()

      runInAction(() => {
        this.pendingEditConfirmation.set({
          commits: result.commits.length,
          resolve: (proceed: boolean) => {
            runInAction(() => {
              this.pendingEditConfirmation.set(null)
            })
            resolve(proceed)
          }
        })
      })

      const proceed = await promise

      if (!proceed) {
        return false
      }

      await this.queries.apiClient.editMessage(messageId, message, context || [], attachmentIds || [], elements || [], true)
    }

    return true
  }

  readonly confirmEditWithRollback = () => {
    const pending = this.pendingEditConfirmation.get()
    if (pending) {
      pending.resolve(true)
    }
  }

  readonly cancelEditConfirmation = () => {
    const pending = this.pendingEditConfirmation.get()
    if (pending) {
      pending.resolve(false)
    }
  }

  readonly loadNext = () => {
    this.scheduleUpdate({ "type": "load-next" })
  }

  readonly loadPrev = () => {
    this.scheduleUpdate({ "type": "load-prev" })
  }

  readonly navigate = (messageId: string) => {
    this.scheduleUpdate({ "type": "reload", messageId })
  }

  private listenForUpdates(initialMessageId: string | null) {
    const updatesChannel = this.queries.messageUpdates(this.taskId)
    this.scheduleUpdate({ type: "reload", messageId: initialMessageId })

    this.lifetime.add(updatesChannel.subscribe(async event => {
      if (event.className === "MessageEvent") {
        const messageEvent: AiChatEvent.MessageEvent = event as AiChatEvent.MessageEvent

        const isUserMessage = messageEvent.message.author.className === "Principal"
        runInAction(() => {
          if (isUserMessage) {
            this.waitingForResponse.set(this.waitingForResponse.get() + 1)
          } else {
            this.waitingForResponse.set(0)
          }
        })

        if ((messageEvent.modType === "Created" || messageEvent.modType === "Updated") && this.rawMessages.has(messageEvent.message.id)) {
          if (compareStringAsInteger(this.rawMessages.get(messageEvent.message.id)!.timestamp, messageEvent.message.timestamp) < 0) {
            await this.consumeBatch([messageEvent.message])
          }
          this.scheduleUpdate({ type: "update-message", messageId: messageEvent.message.id })
        } else if (messageEvent.modType === "Deleted") {
          await this.removeBatch([messageEvent.message])
        } else if (!this.hasNext.get()) {
          this.scheduleUpdate({ type: "update-recent" })
        }
      } else if (event.className === "SummaryEvent") {
        this.summaryModelEvents.next(event as AiChatEvent.SummaryEvent)
      } else if (event.className === "CheckpointReset") {
        this.scheduleUpdate({ type: "reload", messageId: null })
      }
    }), "ChatModelImpl:updatesChannel")
  }

  terminate() {
    const pending = this.pendingEditConfirmation.get()
    if (pending) {
      pending.resolve(false)
    }
    super.terminate()
    this.typingIndicatorModel.terminate()
    this.updateQueue.length = 0
    this.readDebounced.flush()
  }

  readonly read = (messageId: string) => {
    if (!this.lastReadMessageId || compareStringAsInteger(messageId, this.lastReadMessageId) > 0) {
      this.readDebounced(messageId)
    }
  }

  private readonly readDebounced = debounce((messageId: string) => {
    log.info(`Read message (id=${messageId})`)
    void this.queries.apiClient.readMessage(messageId)
  }, 500, {
    maxWait: 5_000
  })

  private hasPendingReload() {
    return this.updateQueue.some(request => request.type === "reload")
  }

  private initDebugLiveUpdates() {
    const updates = this.queries.liveUpdateDebugEvents()
    let timeout: NodeJS.Timeout | null = null

    this.lifetime.add(updates.subscribe(it => {
      log.debug(`Received live update: ${formatDateTime(it)}`)
      runInAction(() => {
        this.liveUpdateDebugEvents.set(true)
      })
      if (timeout) {
        clearTimeout(timeout)
      }
      timeout = setTimeout(() => {
        runInAction(() => {
          this.liveUpdateDebugEvents.set(false)
        })
      }, 3000)
    }), "liveUpdatesImitator")
  }

  private async startQueueProcessing() {
    if (this.isProcessing) {
      return
    }

    this.isProcessing = true
    while (this.updateQueue.length > 0) {
      try {
        const request = this.updateQueue.shift()!
        log.debug("Process event", request)
        if (request.type === "update-recent") {
          await this.load(request)
        } else if (request.type === "reload") {
          await this.load(request)
        } else if (request.type === "load-next" && !this.isLoadingNext.get() && this.hasNext.get()) {
          await this.load(request)
        } else if (request.type === "load-prev" && !this.isLoadingPrev.get() && this.hasPrev.get()) {
          await this.load(request)
        } else if (request.type === "update-message") {
          await this.updateMessage(request.messageId)
        } else if (request.type === "observable-update") {
          await this.updateMessageList()
        }
      } catch (ex) {
        log.error("Error during queue processing:", ex)
      }
    }

    this.isProcessing = false
  }

  private scheduleUpdate(update: ChatModelUpdateRequest) {
    for (const existingUpdate of this.updateQueue) {
      if (isEqual(existingUpdate, update)) {
        return
      }

      if (existingUpdate.type === "reload" && update.type !== "reload") {
        return
      }
    }

    if (update.type === "reload") {
      this.updateQueue.length = 0
    }

    this.updateQueue.push(update)

    if (!this.isProcessing) {
      void this.startQueueProcessing()
    }
  }

  private getLatestMessageId(ignoreCreditsSpent: boolean = false): string | null {
    if (this.rawMessages.size == 0) {
      return null
    }

    const filteredMessages = [...this.rawMessages.values()]
      .filter((msg) => !(ignoreCreditsSpent && msg.content.className === "CreditsSpent"))

    if (filteredMessages.length == 0) {
      return null
    }

    return filteredMessages
      .reduce((max, current) =>
        compareStringAsInteger(current.longId, max.longId) > 0 ? current : max
      ).id
  }

  private getOldestMessageId(): string | null {
    if (this.rawMessages.size == 0) {
      return null
    }

    return [...this.rawMessages.values()].reduce((min, current) =>
      compareStringAsInteger(current.longId, min.longId) < 0 ? current : min
    ).id
  }

  private async consumeBatch(batch: AiChatMessage[], isUpdatingRecent: boolean = false) {
    const latestMessageId = this.getLatestMessageId(true)
    for (const item of batch) {
      if (!this.rawMessages.has(item.id) && isUpdatingRecent && (latestMessageId == null || compareStringAsInteger(item.longId, this.rawMessages.get(latestMessageId)!.longId) > 0)) {
        this.newMessages.add(item.id)
      }
      this.rawMessages.set(item.id, item)
    }

    await this.updateMessageList()
  }

  private async removeBatch(batch: AiChatMessage[]) {
    log.debug("Remove", batch.map(messageAsText))
    for (const item of batch) {
      this.rawMessages.delete(item.id)
    }

    await this.updateMessageList()
  }

  private resetState(messageId: string | null = null) {
    runInAction(() => {
      this.rawMessages.clear()
      this.updateQueue.length = 0
      this.hasNext.set(messageId !== null)
      this.hasPrev.set(true)
    })
  }

  private async getBatch(offset: string | null, sorting: MessagesSorting, loadParams: LoadParams): Promise<Batch<AiChatMessage>> {
    const type = loadParams.type
    const batchInfo: BatchInfo = { offset, batchSize: this.batchSize }
    const batch = (await this.queries.messages(this.taskId, batchInfo, sorting))
    if (batch.data.length < this.batchSize) {
      if (sorting === "FromNewestToOldest") {
        runInAction(() => this.hasPrev.set(false))
      } else if (sorting === "FromOldestToNewest") {
        runInAction(() => this.hasNext.set(false))
      }
    } else if (type === "update-recent") {
      let newMessagesCount = 0
      for (const message of batch.data) {
        if (!this.rawMessages.has(message.id)) {
          newMessagesCount++
        }
      }

      if (newMessagesCount >= this.batchSize) {
        // Too many updates, try to reload
        this.resetState()
      }
    }

    return batch
  }

  private async load(params: LoadParams) {
    const type = params.type
    let sorting: MessagesSorting | null = null
    let offset: string | null = null
    let loadNextAdditionally: boolean = false

    runInAction(() => {
      this.isLoadingNext.set(type === "load-next")
      this.isLoadingPrev.set(type === "load-prev")
    })

    if (type === "reload") {
      this.resetState(params.messageId)
    }

    try {
      if (type === "load-prev") {
        sorting = "FromNewestToOldest"
        offset = this.getOldestMessageId()
      } else if (type === "load-next") {
        sorting = "FromOldestToNewest"
        offset = this.getLatestMessageId()
      } else if (type === "update-recent") {
        sorting = "FromNewestToOldest"
        offset = null
      } else if (type === "reload") {
        sorting = "FromNewestToOldest"
        if (params.messageId) {
          offset = params.messageId
          loadNextAdditionally = true
          this.messageIdToFocusId.set(params.messageId, nextFocusId())
        }
      } else {
        return
      }

      const batch = await this.getBatch(offset, sorting, params)
      let messages: AiChatMessage[] = [...batch.data]
      if (loadNextAdditionally) {
        log.info("Load next additionally", batch)
        const nextBatch = await this.getBatch(batch.next, "FromOldestToNewest", params)
        messages = [...batch.data, ...nextBatch.data]
      }

      await this.consumeBatch(messages, type === "update-recent")
    } finally {
      runInAction(() => {
        this.isLoadingPrev.set(false)
        this.isLoadingNext.set(false)
        this.isLoadingInitially.set(false)

        if (type === "reload" && !this.hasPendingReload()) {
          this.isResettingCheckpoint.set(false)
        }

        this.isReady.set(true)
      })
    }
  }

  private async updateMessage(messageId: string) {
    const updatedMessage = await this.queries.message(messageId)
    if (!this.hasPendingReload()) {
      await this.consumeBatch([updatedMessage])
    }
  }

  private async updateMessageList() {
    const messages = await this.createMessages({
      messages: Array.from(this.rawMessages.values()),
      latestMessageId: this.getLatestMessageId(true),
      active: true,
      editable: true
    })
    log.debug(`Total message count: ${messages.length}`)

    runInAction(() => this.messages.set(messages))
  }

  private async createMessages(params: {
    messages: AiChatMessage[],
    editable: boolean,
    latestMessageId: string | null,
    active: boolean,
  }): Promise<ChatMessageModel[]> {
    this.extender.resetTouch()

    const { messages, editable, latestMessageId, active } = params
    const activeEditingModel = this.activeEditingModel.get()
    const result: ChatMessageModel[] = []
    const myPrincipal = await this.myPrincipal

    await this.prefillPrincipalsCache(messages)

    messages.sort(messageComparator)

    let isPastEditingMessage = false

    for (const [message, index] of messages.map((it, index) => [it, index] as const)) {
      let user: TD_MemberProfile | null = null

      if (message.author.className === "Principal") {
        const principalAuthor = message.author as AiChatMessageAuthor.Principal
        const principal = principalCache.get(principalAuthor.pid)

        if (isUserPrincipal(principal)) {
          user = principal.details.user
        }
      }
      const isMyMessage = message.author.className === "Principal" && (message.author as AiChatMessageAuthor.Principal).pid === myPrincipal.id
      const nextMessage: ChatMessageModel = {
        ...message,
        user,
        isNew: this.newMessages.has(message.id),
        isLast: latestMessageId === message.id,
        focusId: this.messageIdToFocusId.get(message.id) ?? null,
        active: active && !isPastEditingMessage,
        editingModel: activeEditingModel && activeEditingModel.messageId === message.id ? activeEditingModel : null,
        isMyMessage,
        text: copyableTextContent(message.content),
        edit: (this.hasPrev.get() || index !== 0) && editable && isMyMessage && message.content.className === "Text" && this.readOnly?.get() !== true ? () => this.edit(message.id, message.content as AiChatMessageContent.Text) : null,
        onRetryProcess: () => this.retryTaskProcess(message.id),
        onContinueProcess: () => this.continueTaskProcess(message.id),
        downloadAttachment: (file) => this.queries.apiClient.downloadFile(message.taskId, file.id),
      }

      isPastEditingMessage = isPastEditingMessage || nextMessage.editingModel !== null

      result.push(nextMessage)
    }

    this.combineAdjacentArchivedBlocks(result)
    this.enhanceWithArchivedBlockModel(result)
    this.extender.clearUntouched()

    return result
  }

  private async retryTaskProcess(messageId: string) {
    await this.queries.apiClient.retryTaskProcess(this.taskId, messageId)
  }

  private async continueTaskProcess(messageId: string) {
    await this.queries.apiClient.continueTaskProcess(this.taskId, messageId)
  }

  private async prefillPrincipalsCache(messages: AiChatMessage[]) {
    const principalIds = new Set<string>()

    for (const message of messages) {
      if (message.author.className === "Principal") {
        const principalAuthor = message.author as AiChatMessageAuthor.Principal
        principalIds.add(principalAuthor.pid)
      }
    }

    const unresolvedPrincipalIds = Array.from(principalIds).filter(pid => !principalCache.has(pid))
    if (unresolvedPrincipalIds.length > 0) {
      const principalDetails = await this.queries.resolvePrincipals(unresolvedPrincipalIds)
      for (const pair of principalDetails) {
        principalCache.set(pair.first, pair.second)
      }
    }
  }

  private combineAdjacentArchivedBlocks(result: ChatMessageModel[]) {
    const messages = [...result]
    result.length = 0
    for (const message of messages) {
      if (message.content.className === "ArchivedBlock") {
        const last = result.length > 0 ? result[result.length - 1] : null

        if (last && last.content.className === "ArchivedBlockGroup") {
          const prevContent = last.content
          result.pop()
          result.push({
            ...message,
            content: {
              className: "ArchivedBlockGroup",
              blocks: [...prevContent.blocks, message.content],
            }
          })
        } else {
          result.push({
            ...message,
            content: {
              className: "ArchivedBlockGroup",
              blocks: [message.content],
            }
          })
        }
      } else {
        result.push(message)
      }
    }
  }

  private enhanceWithArchivedBlockModel(result: ChatMessageModel[]) {
    for (let i = 0; i < result.length; i++) {
      const message = result[i]
      const content = message.content
      if (content.className === "ArchivedBlockGroup") {
        result[i] = {
          ...result[i],
          archivedBlockModel: this.extender.get(message.id, (message.content as AiChatMessageContent.ArchivedBlockGroup).blocks, () => {
            return new ArchivedMessagesBlockModel(this.queries, this.taskId, content, (messages) => {
              return this.createMessages({
                messages,
                editable: false,
                latestMessageId: messages.length > 0 ? messages[messages.length - 1].id : null,
                active: false
              })
            })
          })
        }
      }
    }
  }
}

type LoadParams =
  | { type: "update-recent" }
  | { type: "reload", messageId: string | null }
  | { type: "load-next" }
  | { type: "load-prev" }

type ChatModelUpdateRequest =
  | { type: "update-recent" }
  | { type: "reload", messageId: string | null }
  | { type: "load-prev" }
  | { type: "load-next" }
  | { type: "update-message", messageId: string }
  | { type: "observable-update" }

export function messageAsText(message: AiChatMessage) {
  if (message.content.className === "Text") {
    return (message.content as AiChatMessageContent.Text).text
  }

  if (message.content.className === "AgentText") {
    return (message.content as AiChatMessageContent.AgentText).text
  }

  return JSON.stringify(message.content)
}

const messageComparator: (a: AiChatMessage, b: AiChatMessage) => number = (a, b) => {
  return compareStringAsInteger(a.longId, b.longId)
}

export const principalCache = new Map<string, CPrincipal>()

function copyableTextContent(content: UAiChatMessageContent): string | null {
  if (content.className === "Text") {
    return content.text.value
  }

  if (content.className === "AgentText") {
    return content.text.value
  }

  return null
}